import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserRound } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import TurnstileWidget from "@/components/TurnstileWidget";
import { countryOptions } from "@/countries";

export default function AuthPage() {
  const { t, lang } = useI18n();
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Return target after sign-in (e.g. /checkout?plan=annual) — local paths only.
  const rawNext = params.get("next") || "";
  const next = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : null;
  const afterAuth = async (role) => {
    // Le compte fraîchement identifié doit apparaître tout de suite dans l'en-tête :
    // la lecture partagée est rafraîchie avant la navigation.
    await refreshMe();
    navigate(next || (role === "admin" ? "/admin" : "/"));
  };
  // ?mode=register lands straight on account creation (alert CTAs must not dump a
  // visitor with no account on a bare sign-in form).
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("FR");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loginCaptcha, setLoginCaptcha] = useState(false);
  const widget = useRef(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const onTurnstile = useCallback((token) => setTurnstileToken(token), []);
  const onTurnstileInvalid = useCallback(() => setTurnstileToken(null), []);

  // Login: the captcha appears only after repeated failures (the server decides too).
  useEffect(() => {
    if (mode !== "login" || !email.includes("@")) return;
    api.get(`/auth/login-challenge?email=${encodeURIComponent(email)}`)
      .then((r) => setLoginCaptcha(!!r.data.captcha_required))
      .catch(() => {});
  }, [mode, email]);

  // Only ONE widget may be mounted at a time: register and forgot always, login on demand.
  const captchaMode = mode === "register" || mode === "forgot" || (mode === "login" && loginCaptcha);

  const mapError = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail === "email_not_verified") return "email_not_verified";
    if (detail === "code_invalid") return t("auth.code_invalid");
    if (detail === "code_expired") return t("auth.code_expired");
    if (detail === "too_many_code_attempts") return t("auth.too_many_attempts");
    if (detail === "resend_too_soon") return t("auth.resend_wait");
    if (detail === "captcha_failed") return t("auth.captcha_failed");
    if (detail === "captcha_required") return t("auth.captcha_required");
    return formatApiError(err);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setInfo("");
    try {
      if (mode === "register") {
        await api.post("/auth/register", { first_name: firstName, last_name: lastName, country, email, password, turnstile_token: turnstileToken, lang });
        setMode("verify");
        setInfo(t("auth.verify_sent"));
        setResendIn(60);
      } else if (mode === "forgot") {
        // Same confirmation whatever the outcome — never reveals if the address exists.
        await api.post("/auth/forgot-password", { email, lang, turnstile_token: turnstileToken });
        setInfo(t("auth.reset_sent"));
      } else if (mode === "login") {
        const r = await api.post("/auth/login", { email, password, turnstile_token: turnstileToken });
        afterAuth(r.data.role);
      } else {
        const r = await api.post("/auth/verify-email", { email, code });
        afterAuth(r.data.role);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (mode === "login" && (detail === "captcha_required" || err?.response?.status === 401)) {
        // Escalation is server-side; ask the server again so the widget shows up in time.
        api.get(`/auth/login-challenge?email=${encodeURIComponent(email)}`)
          .then((r) => setLoginCaptcha(!!r.data.captcha_required))
          .catch(() => {});
      }
      const mapped = mapError(err);
      if (mapped === "email_not_verified") {
        setMode("verify");
        setInfo(t("auth.verify_sent"));
      } else {
        setError(mapped);
      }
    } finally {
      setBusy(false);
      // A Turnstile token is single-use: reset after EVERY submission, success or failure,
      // otherwise the next attempt dies on "human verification failed".
      widget.current?.reset();
      setTurnstileToken(null);
    }
  };

  const resend = async () => {
    setError("");
    try {
      await api.post("/auth/resend-code", { email, lang });
      setResendIn(60);
      setInfo(t("auth.verify_sent"));
    } catch (err) {
      setError(mapError(err));
    }
  };

  const inputCls =
    "w-full rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2.5 text-sm text-slate-200 focus:border-[#00FF87] focus:outline-none";

  const titles = { login: t("nav.sign_in"), register: t("auth.register"), verify: t("auth.verify_title"), forgot: t("auth.forgot_title") };

  return (
    <div className="flex justify-center pt-16" data-testid="auth-page">
      <form onSubmit={submit} className="hud-card w-full max-w-sm rounded-xl p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#00FF87]/40 bg-[#00FF87]/15">
            <UserRound className="h-4 w-4 text-[#00FF87]" />
          </span>
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{titles[mode]}</h1>
        </div>

        {mode === "verify" && (
          <>
            <p className="mb-4 text-xs leading-relaxed text-slate-400">{t("auth.verify_text", { email })}</p>
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("auth.code_label")}</label>
            <input
              data-testid="verify-code-input"
              required
              inputMode="numeric"
              maxLength={6}
              className={`${inputCls} mb-5 text-center font-mono2 text-2xl tracking-[0.5em]`}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          </>
        )}

        {mode === "forgot" && (
          <>
            <p className="mb-4 text-xs leading-relaxed text-slate-400">{t("auth.forgot_text")}</p>
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.email")}</label>
            <input data-testid="forgot-email-input" type="email" required className={`${inputCls} mb-5`} value={email} onChange={(e) => setEmail(e.target.value)} />
          </>
        )}

        {mode !== "verify" && mode !== "forgot" && (
          <>
            {mode === "register" && (
              <>
                <div className="mb-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("auth.first_name")}</label>
                    <input data-testid="auth-first-name-input" required className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div>
                    <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("auth.last_name")}</label>
                    <input data-testid="auth-last-name-input" required className={inputCls} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("auth.country")}</label>
                <select data-testid="auth-country-select" required className={`${inputCls} mb-4`} value={country} onChange={(e) => setCountry(e.target.value)}>
                  {countryOptions(lang).map((c) => <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>)}
                </select>
              </>
            )}
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.email")}</label>
            <input data-testid="auth-email-input" type="email" required className={`${inputCls} mb-4`} value={email} onChange={(e) => setEmail(e.target.value)} />
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.password")}</label>
            <input data-testid="auth-password-input" type="password" required minLength={mode === "register" ? 8 : 1} className={`${inputCls} mb-2`} value={password} onChange={(e) => setPassword(e.target.value)} />
            {mode === "login" && (
              <div className="mb-4 text-right">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                  className="text-xs text-slate-400 hover:text-[#00FF87]"
                >
                  {t("auth.forgot_link")}
                </button>
              </div>
            )}
            {captchaMode && mode !== "forgot" && (
              <div className="mb-3">
                <TurnstileWidget ref={widget} action={mode} onToken={onTurnstile} onInvalid={onTurnstileInvalid} />
              </div>
            )}
          </>
        )}

        {/* Forgot-password: always challenged, and the only widget mounted in that mode. */}
        {mode === "forgot" && (
          <div className="mb-3">
            <TurnstileWidget ref={widget} action="forgot" onToken={onTurnstile} onInvalid={onTurnstileInvalid} />
          </div>
        )}

        {error && (
          <p className="mb-4 rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-400" data-testid="auth-error">
            {error}
          </p>
        )}
        {info && (
          <p className="mb-4 rounded-md border border-[#00FF87]/40 bg-[#00FF87]/10 px-3 py-2 text-xs text-[#00FF87]" data-testid="auth-info">
            {info}
          </p>
        )}

        <button
          data-testid="auth-submit-button"
          type="submit"
          disabled={busy || (captchaMode && !turnstileToken) || (mode === "verify" && code.length !== 6)}
          className="btn-volt w-full disabled:opacity-50"
        >
          {busy ? t("common.loading") : mode === "verify" ? t("auth.verify_btn") : mode === "register" ? t("auth.register") : mode === "forgot" ? t("auth.forgot_btn") : t("nav.sign_in")}
        </button>

        {mode === "verify" && (
          <button
            type="button"
            data-testid="resend-code-button"
            onClick={resend}
            disabled={resendIn > 0}
            className="mt-4 w-full text-center text-xs text-slate-400 hover:text-[#00FF87] disabled:opacity-40"
          >
            {resendIn > 0 ? `${t("auth.resend")} (${resendIn}s)` : t("auth.resend")}
          </button>
        )}
        {mode === "forgot" && (
          <button
            type="button"
            data-testid="forgot-back-login"
            onClick={() => { setMode("login"); setError(""); setInfo(""); }}
            className="mt-4 w-full text-center text-xs text-slate-400 hover:text-[#00FF87]"
          >
            {t("auth.back_login")}
          </button>
        )}
        {mode !== "verify" && mode !== "forgot" && (
          <button
            type="button"
            data-testid="auth-mode-toggle"
            onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); setInfo(""); }}
            className="mt-3 w-full rounded-full border border-[#00FF87]/60 px-4 py-2.5 font-mono2 text-[11px] font-bold uppercase tracking-wider text-[#00FF87] transition-colors hover:bg-[#00FF87]/10"
          >
            {mode === "register" ? t("auth.switch_login") : t("auth.switch_register")}
          </button>
        )}
      </form>
    </div>
  );
}
